/**
 * OAuth helper utilities for Google and LinkedIn authentication
 */

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const LINKEDIN_CLIENT_ID = import.meta.env.VITE_LINKEDIN_CLIENT_ID;

const GOOGLE_OAUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const LINKEDIN_OAUTH_URL = 'https://www.linkedin.com/oauth/v2/authorization';

/**
 * Get the current origin for OAuth redirect
 */
function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * Generate a random state parameter for OAuth security
 */
function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Initiate Google OAuth flow
 */
export function initiateGoogleOAuth(): void {
  if (!GOOGLE_CLIENT_ID) {
    console.error('Google Client ID not configured');
    alert('Google OAuth is not configured. Please check environment variables.');
    return;
  }

  const state = generateState();
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: getRedirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    state,
    access_type: 'offline',
    prompt: 'select_account',
  });

  window.location.href = `${GOOGLE_OAUTH_URL}?${params.toString()}`;
}

/**
 * Initiate LinkedIn OAuth flow
 */
export function initiateLinkedInOAuth(): void {
  if (!LINKEDIN_CLIENT_ID) {
    console.error('LinkedIn Client ID not configured');
    alert('LinkedIn OAuth is not configured. Please check environment variables.');
    return;
  }

  const state = generateState();
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id: LINKEDIN_CLIENT_ID,
    redirect_uri: getRedirectUri(),
    response_type: 'code',
    scope: 'openid profile email',
    state,
  });

  window.location.href = `${LINKEDIN_OAUTH_URL}?${params.toString()}`;
}

/**
 * Handle OAuth callback
 * Returns the authorization code if valid, null otherwise
 */
export function handleOAuthCallback(): { code: string; provider: string } | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  const provider = params.get('provider') || 'google';

  // Check for OAuth errors
  if (error) {
    console.error('OAuth error:', error);
    return null;
  }

  // Validate state parameter
  const savedState = sessionStorage.getItem('oauth_state');
  if (!savedState || state !== savedState) {
    console.error('OAuth state mismatch');
    return null;
  }

  // Clean up
  sessionStorage.removeItem('oauth_state');

  if (code) {
    return { code, provider };
  }

  return null;
}
